import { updateSidePanel } from '@/app/store';
import { MESSAGE_EDIT_TIMEOUT_MINUTES } from '@/processENV';
import {
   ArrowBendUpLeftIcon,
   CopyIcon,
   InfoIconNew,
   PencilSimpleLineIcon,
   PreviewForward,
   StarIcon,
   TrashIcon,
   UnStarIcon,
} from '@/assets/images';
import { useChatFeature } from '@/features/chats/chatsRegistry';
import { toggleMessageSelection, useIsAnyMessageSelected } from '@/features/chats/model';
import { useDraft } from '@/features/chats/model/draft/hooks';
import { useIsBlockedByMe } from '@/features/users';
import timeSync from '@/infrastructure/utils/timeSync';
import { useLanguage } from '@/localization/useLanguage';
import {
   blockOfflineAction,
   getLocalUserId,
   getMessageSenderId,
   getUserIdFromJid,
   logger,
   showSuccessToast,
} from '@/shared/utils';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAmIParticipant } from '../model/groupParticipants';
import { toggleStarMessage } from '../utils';
import { fetchReplyMessage } from '../utils/fetchReplyMessage';
import { formatMessageToHtml } from '../utils/htmlParser';
import { formatPlainTextMention } from '../viewmodels/useMessageFormatter';
import { useChatRequest } from './useChatRequest';

const EMPTY_FEATURES = {};

export const useMessageActionDropdown = ({ message, activeChatJid, allowedActions }) => {
   const { t } = useLanguage();
   const dispatch = useDispatch();
   const users = useSelector(state => state.userInfoData?.users || {});
   const activeChatId = useMemo(() => getUserIdFromJid(activeChatJid), [activeChatJid]);
   const messageActionFeatures = useChatFeature('message-action') || EMPTY_FEATURES;
   const { setReplyTo, saveDraft, setEditMessageId } = useDraft({ activeChatId });
   const isAnyMessageSelected = useIsAnyMessageSelected(activeChatId);
   const senderId = getMessageSenderId({ type: 'chats', message: message });
   const isSenderMe = getUserIdFromJid(senderId) === getLocalUserId();
   const isBlockedByMe = useIsBlockedByMe(activeChatId);
   const amIParticipant = useAmIParticipant(activeChatId);
   const isGroup = activeChatJid.includes('mix');
   const { isReceiver } = useChatRequest(activeChatJid);

   // Common variable — group checks participant, user checks block status
   const isReplyDisabled = isGroup ? !amIParticipant : isBlockedByMe;

   const chatMenuItems = useMemo(() => {
      if (isReceiver) return [];

      const { deleteStatus, msgStatus } = message;

      const isNormalMessage = deleteStatus === 0 && msgStatus < 3;
      const isDeletedMessage = deleteStatus === 1;

      if (isAnyMessageSelected || (!isNormalMessage && !isDeletedMessage)) {
         return [];
      }

      const hasContent = message.msgBody?.message || message.msgBody?.media?.caption;
      const timeElapsedInMinutes = (timeSync.getSyncedTime() - Number(message.timestamp)) / (1000 * 60);
      const isEditTimeValid = timeElapsedInMinutes <= MESSAGE_EDIT_TIMEOUT_MINUTES;

      const canShowInfo = messageActionFeatures['message-action-info'] && isSenderMe;
      const canReply = messageActionFeatures?.['message-action-reply'] && !isReplyDisabled;
      const canForward = messageActionFeatures['message-action-forward'];
      const canCopy = hasContent && messageActionFeatures['message-action-copy'];
      const canStar = messageActionFeatures['message-action-star'];
      const canEdit =
         hasContent &&
         messageActionFeatures['message-action-edit'] &&
         isSenderMe &&
         isEditTimeValid &&
         (!isGroup || amIParticipant);
      const canDelete = messageActionFeatures['message-action-delete'];

      const items = [
         canShowInfo && {
            id: 'info',
            icon: <InfoIconNew />,
            label: t('message.ACTION_INFO'),
            action: () =>
               dispatch(
                  updateSidePanel({
                     id: 'message-info',
                     title: t('message.INFO_TITLE'),
                     isOpen: true,
                     data: { msgId: message.msgId, chatJid: activeChatJid },
                  }),
               ),
         },
         canReply && {
            id: 'reply',
            icon: <ArrowBendUpLeftIcon />,
            label: t('message.ACTION_REPLY'),
            action: () => {
               if (blockOfflineAction()) return;
               saveDraft('');
               setEditMessageId(null);
               setReplyTo(message?.editMessageId || message.msgId);
               fetchReplyMessage(message?.editMessageId || message.msgId, activeChatJid);
               document.getElementById('typingContainer')?.focus();
            },
         },
         canForward && {
            id: 'forward',
            icon: <PreviewForward />,
            label: t('message.ACTION_FORWARD'),
            action: () => {
               if (blockOfflineAction()) return;
               dispatch(
                  toggleMessageSelection({
                     chatId: activeChatId,
                     msgId: message.msgId,
                     selectionType: 'forward',
                  }),
               );
            },
         },
         canCopy && {
            id: 'copy',
            icon: <CopyIcon />,
            label: t('message.ACTION_COPY'),
            action: () => {
               const { message: msgContent, media, mentionedUsersIds = [] } = message.msgBody || {};
               const rawContent = msgContent || media?.caption || '';
               const formattedContent = formatPlainTextMention(rawContent, mentionedUsersIds, users);
               navigator.clipboard.writeText(formattedContent).then(() => {
                  showSuccessToast(t('toast.COPY_SUCCESS', { number: 1 }));
                  logger.log('Copied to clipboard successfully!');
               });
            },
         },
         canStar && {
            id: 'star',
            icon: message.favouriteStatus === 1 ? <UnStarIcon /> : <StarIcon />,
            label: message.favouriteStatus === 1 ? t('message.ACTION_UNSTAR') : t('message.ACTION_STAR'),
            action: () => {
               if (blockOfflineAction()) return;
               toggleStarMessage(message, activeChatJid);
            },
         },
         canEdit && {
            id: 'edit',
            icon: <PencilSimpleLineIcon />,
            label: t('message.ACTION_EDIT'),
            action: () => {
               if (blockOfflineAction()) return;
               setReplyTo(null);
               const { message: textMessage = '', media = {}, mentionedUsersIds = [] } = message.msgBody || {};
               const rawContent = textMessage || media?.caption || '';
               const htmlContent = formatMessageToHtml(rawContent, mentionedUsersIds);

               saveDraft(htmlContent);
               setEditMessageId(message.msgId);
               fetchReplyMessage(message.msgId, activeChatJid, true);
            },
         },
         canDelete && {
            id: 'delete',
            icon: <TrashIcon />,
            label: t('message.ACTION_DELETE'),
            action: () => {
               if (blockOfflineAction()) return;
               dispatch(
                  toggleMessageSelection({
                     chatId: activeChatId,
                     msgId: message.msgId,
                     selectionType: 'delete',
                     activeChatId,
                  }),
               );
            },
            className: 'danger',
         },
      ].filter(Boolean);

      if (isDeletedMessage) {
         const deletedItems = items.filter(item => item.id === 'delete');
         if (allowedActions?.length) {
            return deletedItems.filter(item => allowedActions.includes(item.id));
         }
         return deletedItems;
      }

      if (allowedActions?.length) {
         return items.filter(item => allowedActions.includes(item.id));
      }

      return items;
   }, [
      allowedActions,
      isSenderMe,
      activeChatId,
      activeChatJid,
      dispatch,
      isAnyMessageSelected,
      message,
      messageActionFeatures,
      setReplyTo,
      setEditMessageId,
      saveDraft,
      t,
      isReplyDisabled,
      isReceiver,
      users,
      isGroup,
      amIParticipant,
   ]);

   return { chatMenuItems };
};
